/**
 * @service LoggerService
 * @description Centralized logging service for the application
 * @version 1.0.0
 */

export enum LogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug',
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context: string | undefined;
  data: any;
  timestamp: string;
}

class LoggerService {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private logLevel: LogLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;

  /**
   * Log an error message
   * @param message - Error message
   * @param context - Context where error occurred
   * @param data - Additional error data
   */
  error(message: string, context?: string, data?: any): void {
    this.log(LogLevel.ERROR, message, context, data);
  }

  /**
   * Log a warning message
   * @param message - Warning message
   * @param context - Context where warning occurred
   * @param data - Additional warning data
   */
  warn(message: string, context?: string, data?: any): void {
    this.log(LogLevel.WARN, message, context, data);
  }

  /**
   * Log an info message
   * @param message - Info message
   * @param context - Context where info occurred
   * @param data - Additional info data
   */
  info(message: string, context?: string, data?: any): void {
    this.log(LogLevel.INFO, message, context, data);
  }

  /**
   * Log a debug message (only in development)
   * @param message - Debug message
   * @param context - Context where debug occurred
   * @param data - Additional debug data
   */
  debug(message: string, context?: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, context, data);
  }

  /**
   * Core logging method
   * @param level - Log level
   * @param message - Log message
   * @param context - Context
   * @param data - Additional data
   */
  private log(level: LogLevel, message: string, context?: string, data?: any): void {
    // Skip debug logs in production
    if (!this.isDevelopment && level === LogLevel.DEBUG) {
      return;
    }

    const logEntry: LogEntry = {
      level,
      message,
      context,
      data,
      timestamp: new Date().toISOString(),
    };

    // In development, use console
    if (this.isDevelopment) {
      this.logToConsole(logEntry);
    }

    // In production, send to logging service
    if (!this.isDevelopment) {
      this.sendToLoggingService(logEntry);
    }
  }

  /**
   * Log to console (development only)
   * @param entry - Log entry
   */
  private logToConsole(entry: LogEntry): void {
    const { level, message, context, data, timestamp } = entry;
    const prefix = `[${timestamp}] [${level.toUpperCase()}]${context ? ` [${context}]` : ''}`;
    
    switch (level) {
      case LogLevel.ERROR:
        // eslint-disable-next-line no-console
        console.error(prefix, message, data || '');
        break;
      case LogLevel.WARN:
        // eslint-disable-next-line no-console
        console.warn(prefix, message, data || '');
        break;
      case LogLevel.INFO:
        // eslint-disable-next-line no-console
        console.info(prefix, message, data || '');
        break;
      case LogLevel.DEBUG:
        // eslint-disable-next-line no-console
        console.debug(prefix, message, data || '');
        break;
    }
  }

  /**
   * Send log to external logging service (production)
   * @param entry - Log entry
   */
  private sendToLoggingService(entry: LogEntry): void {
    // In production, you would send this to your logging service
    // For now, we'll just store it locally or send to an API
    try {
      // Example: Send to logging API
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry)
      // });
      
      // For now, just store in localStorage for debugging
      const logs = JSON.parse(localStorage.getItem('app_logs') || '[]');
      logs.push(entry);
      if (logs.length > 100) {
        logs.splice(0, logs.length - 100); // Keep only last 100 logs
      }
      localStorage.setItem('app_logs', JSON.stringify(logs));
    } catch (error) {
      // Fallback to console if logging service fails
      // eslint-disable-next-line no-console
      console.error('Failed to send log to service:', error);
    }
  }

  /**
   * Get stored logs (for debugging)
   * @returns Array of log entries
   */
  getLogs(): LogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('app_logs') || '[]');
    } catch {
      return [];
    }
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    localStorage.removeItem('app_logs');
  }

  /**
   * Set log level
   * @param level - New log level
   */
  setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }
}

// Export singleton instance
export const logger = new LoggerService();

// Convenience exports
export const { error, warn, info, debug } = logger;
